import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import { DeskTable, type Column } from '@/components/common/DeskTable'
import PageFrame from '@/components/common/PageFrame'
import StatusBadge from '@/components/common/StatusBadge'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { backofficeApi, type RateRow } from '@/lib/api-client'
import { fieldErrors, unmatchedFieldErrors } from '@/lib/errors'
import { fmtNum } from '@/lib/format'
import { FORM_CARD } from '@/lib/ui-shell'
import { parseRate, rateRangeError, RATE_MAX, RATE_MIN } from '@/lib/validate'

/** No single rate is in effect. Mark the newest active row; highest rate breaks a timestamp tie. */
export function latestActiveRateId(rows: RateRow[]): string | null {
  const active = rows.filter((r) => r.is_active)
  if (!active.length) return null
  return [...active].sort((a, b) => {
    const at = a.created_at ?? ''
    const bt = b.created_at ?? ''
    if (at !== bt) return at < bt ? 1 : -1
    return b.rate - a.rate
  })[0].id
}

export function RatesPage() {
  const qc = useQueryClient()
  const [sp, setSp] = useSearchParams()
  useEffect(() => {
    if (!sp.has('sort') && !sp.has('order') && !sp.has('dir')) return
    const next = new URLSearchParams(sp)
    next.delete('sort')
    next.delete('order')
    next.delete('dir')
    setSp(next, { replace: true })
  }, [sp, setSp])
  const q = useQuery({ queryKey: ['rates'], queryFn: () => backofficeApi.listRates() })
  const [rate, setRate] = useState('') // never prefilled: empty must not turn into a value
  const [submitted, setSubmitted] = useState(false)
  const [target, setTarget] = useState<RateRow | null>(null)
  const [remove, setRemove] = useState<RateRow | null>(null)
  const sortedRates = [...(q.data?.items ?? [])].sort((a, b) => a.rate - b.rate)
  const latestId = latestActiveRateId(q.data?.items ?? [])
  const columns: Column<RateRow>[] = [
    {
      key: 'rate',
      header: 'Rate (IDR per 1 USD)',
      sticky: true,
      mobile: 'title',
      align: 'right',
      className: 'w-40',
      cell: (r) => (
        <span className="flex w-full items-center justify-end gap-2">
          <span className="inline-flex w-16 shrink-0 justify-start">
            {r.id === latestId ? (
              <Badge title="Most recently added active rate" variant="outline">
                Latest
              </Badge>
            ) : null}
          </span>
          <span className="min-w-0 flex-1 text-right">{fmtNum(r.rate)}</span>
        </span>
      ),
    },
    { key: 'status', header: 'Status', mobile: 'status', cell: (r) => <StatusBadge status={r.is_active ? 'active' : 'inactive'} /> },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      mobile: 'actions',
      cell: (r) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => setTarget(r)}>
            {r.is_active ? 'Disable' : 'Enable'}
          </Button>
          {r.is_active ? null : (
            <Button size="sm" variant="destructive" onClick={() => setRemove(r)}>
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ]

  const refresh = () => void qc.invalidateQueries({ queryKey: ['rates'] })

  const parsed = parseRate(rate)
  const add = useMutation({
    mutationFn: (value: number) => backofficeApi.addRate(value),
    onSuccess: () => {
      refresh()
      setRate('')
      setSubmitted(false)
    },
  })
  const patch = useMutation({
    mutationFn: (p: { id: string; is_active: boolean }) => backofficeApi.patchRate(p.id, p.is_active),
    onSuccess: () => {
      refresh()
      setTarget(null)
    },
  })
  const removeRate = useMutation({
    mutationFn: (row: RateRow) => backofficeApi.deleteRate(row.id),
    onSuccess: () => {
      refresh()
      setRemove(null)
    },
  })

  const server = fieldErrors(add.error)
  const error = (submitted ? parsed.error : undefined) ?? (server.fields.rate ? rateRangeError() : undefined)
  const stray = unmatchedFieldErrors(add.error, ['rate'])
  const formMessage = stray.length
    ? stray.join('; ')
    : add.error && !server.fields.rate
      ? (add.error as Error).message
      : null

  return (
    <PageFrame
      title="Exchange Rates"
      description="Rate tiers in IDR per 1 USD. New rates start inactive until you enable them."
    >
      <form
        noValidate
        className={`${FORM_CARD} flex flex-wrap items-start gap-3`}
        onSubmit={(e) => {
          e.preventDefault()
          setSubmitted(true)
          add.reset()
          if (parsed.value !== undefined) add.mutate(parsed.value)
        }}
      >
        <Field
          label="Rate (IDR per 1 USD)"
          className="w-56"
          error={error}
          hint={
            parsed.value !== undefined
              ? `IDR ${fmtNum(parsed.value)} per 1 USD`
              : `Between ${fmtNum(RATE_MIN)} and ${fmtNum(RATE_MAX)}`
          }
        >
          <Input inputMode="numeric" value={rate} onChange={(e) => setRate(e.target.value)} />
        </Field>
        {/* Offset by the label's height, so the button sits beside the input rather than the hint below it. */}
        <Button type="submit" className="mt-[1.625rem]" disabled={add.isPending}>
          Add rate
        </Button>
      </form>
      {formMessage ? (
        <p role="alert" className="text-sm font-semibold text-destructive">
          {formMessage}
        </p>
      ) : null}
      <DeskTable
        columns={columns}
        rows={sortedRates}
        rowKey={(r) => r.id}
        loading={q.isLoading}
        error={q.error}
        emptyTitle="No exchange rates"
        emptyDescription="Add a rate above. It starts inactive until you enable it."
      />

      <ConfirmDialog
        open={target !== null}
        title={target?.is_active ? 'Disable this rate?' : 'Enable this rate?'}
        summary={target ? <>IDR <span className="font-mono text-foreground">{fmtNum(target.rate)}</span> per 1 USD</> : null}
        confirmLabel={target?.is_active ? 'Disable' : 'Enable'}
        tone={target?.is_active ? 'danger' : 'primary'}
        pending={patch.isPending}
        error={patch.error ? (patch.error as Error).message : null}
        onCancel={() => {
          setTarget(null)
          patch.reset()
        }}
        onConfirm={() => target && patch.mutate({ id: target.id, is_active: !target.is_active })}
      />
      <ConfirmDialog
        open={remove !== null}
        title="Delete this rate?"
        summary={remove ? <>IDR <span className="font-mono text-foreground">{fmtNum(remove.rate)}</span> per 1 USD</> : null}
        confirmLabel="Delete"
        tone="danger"
        pending={removeRate.isPending}
        error={removeRate.error ? (removeRate.error as Error).message : null}
        onCancel={() => {
          setRemove(null)
          removeRate.reset()
        }}
        onConfirm={() => remove && removeRate.mutate(remove)}
      />
    </PageFrame>
  )
}
